import {
  useApiUrl,
  useCreate,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
  useWarnAboutChange,
} from "@refinedev/core";
import { z } from "zod";

import { useEditCreated } from "@/components/common/enrichment-async-hint";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { parseRead } from "@/lib/zod/read-schema";
import {
  Coords,
  NearestGeodata,
  buildNearestGeodataQuery,
  parseNearestGeodata,
} from "@/pages/geodata/lib/nearest-geodata";
import {
  GeodataEditFormType,
  GeodataGeoDbResultType,
  GeodataViewType,
  geodataEditSchema,
  geodataGeoDbResultSchema,
  geodataViewSchema,
} from "@/pages/geodata/schemas/geodata.edit.form";

export const useGeodataRepository = () => {
  const apiUrl = useApiUrl();
  const { getOne, getList, custom } = useDataProvider()();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "geodata" });
  const { mutateAsync: createAsync } = useCreate({ resource: "geodata" });
  const { open } = useNotification();
  const { edit } = useNavigation();
  const editCreated = useEditCreated();
  const { setWarnWhen } = useWarnAboutChange();

  async function get(id: string): Promise<GeodataViewType> {
    const response = await getOne({ id, resource: "geodata" });
    // Lenient read + raw fallback — see `@/lib/zod/read-schema`.
    return parseRead(geodataViewSchema, response, "geodata");
  }

  async function triggerEnrich(id: string): Promise<boolean> {
    try {
      await custom?.({
        url: `${apiUrl}/geodata/enrich`,
        method: "post",
        payload: { id },
      });
      open?.({
        type: "success",
        message: "Enrichment triggered",
        description: "Geodata enrichment has been queued for processing.",
      });
      return true;
    } catch (error) {
      console.error("Failed to trigger geodata enrichment", id, error);
      open?.({
        type: "error",
        message: "Failed to trigger enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function search(query: string): Promise<GeodataEditFormType[]> {
    if (!query) {
      return [];
    }

    const response = await getList({
      resource: "geodata",
      pagination: {
        currentPage: 0,
        pageSize: 25,
      },
      filters: [],
      meta: {
        sortBy: "_text_match:desc",
        query: query,
        queryBy: "name",
      },
    });

    return response.data.map((item: any) =>
      geodataEditSchema.parse(item.document),
    );
  }

  async function searchNearest(
    coords: Coords,
    opts: { radiusKm?: number; limit?: number } = {},
  ): Promise<NearestGeodata[]> {
    const displayLimit = opts.limit ?? 2;
    // Over-fetch so hidden records dropped client-side don't starve the list.
    const query = buildNearestGeodataQuery(coords, {
      radiusKm: opts.radiusKm,
      limit: displayLimit + 3,
    });
    // Route via meta.urlPath (not a full `url`): the provider builds
    // `${apiUrl}/${urlPath}`, whereas a full url gets a trailing slash appended
    // (".../geodata/explore/") that the endpoint 404s on.
    const response = await custom?.({
      url: "",
      method: "get",
      meta: { urlPath: "geodata/explore" },
      query,
    });
    return parseNearestGeodata(response?.data?.hits, coords, displayLimit);
  }

  async function getUsageCount(
    name: string,
  ): Promise<{ events: number; venues: number }> {
    const eventsResult = await getList?.({
      resource: "events",
      pagination: {
        pageSize: 1,
        currentPage: 0,
      },
      filters: [
        {
          field: "geodata.name",
          operator: "eq",
          value: name,
        },
      ],
      meta: {
        includeFields: ["id"],
      },
    });

    const venuesResult = await getList?.({
      resource: "venues",
      pagination: {
        pageSize: 1,
        currentPage: 0,
      },
      filters: [
        {
          field: "geodata.name",
          operator: "eq",
          value: name,
        },
      ],
      meta: {
        includeFields: ["id"],
      },
    });

    return {
      events: eventsResult?.total || 0,
      venues: venuesResult?.total || 0,
    };
  }

  async function create(values: GeodataEditFormType) {
    if (
      !values.geolocation?.latitude ||
      !values.geolocation?.longitude ||
      !values.timezone
    ) {
      open?.({
        type: "error",
        message: "Geolocation and timezone are required",
        description:
          "Please provide both geolocation and timezone to create geodata.",
      });
      return;
    }
    const response = await createAsync?.(
      {
        values,
      },
      {
        onSuccess: (data) => {
          console.debug("Successful geodata creation", data);
          const newId = (
            (data as any)?.id ?? (data as any)?.data?.id
          )?.toString();
          open?.({
            type: "success",
            message: "Successfully created geodata",
            description: `You created a geodata called ${values.name}`,
          });
          if (newId) {
            // clear the unsaved-changes flag so the redirect isn't blocked
            setWarnWhen(false);
            // `?created=1` — the just-created signal GeodataPreview reads.
            // Geodata's create was the LAST synchronous enrichment; now this
            // redirect lands on an unenriched row.
            editCreated("geodata", newId);
          }
        },
        onError: (error) => {
          console.error("Failed to create geodata with values", values, error);
          if (error instanceof z.ZodError) {
            open?.({
              type: "error",
              message: "Create geodata request failed due to step validation",
              description: toHumanErrorMessage(error),
            });
          } else {
            open?.({
              type: "error",
              message: "Create geodata request failed",
              description: toHumanErrorMessage(error),
            });
          }
        },
      },
    );
  }

  async function update(values: GeodataEditFormType) {
    const response = await updateAsync?.(
      {
        id: values.id,
        values,
      },
      {
        onSuccess: (data) => {
          console.debug("Successful geodata update", data);
          edit("geodata", values.id, "replace");
          open?.({
            type: "success",
            message: "Successfully updated geodata",
            description: `You updated a geodata called ${values.name}`,
          });
        },
        onError: (error) => {
          console.error("Failed to update geodata with values", values, error);
          if (error instanceof z.ZodError) {
            open?.({
              type: "error",
              message: "Update geodata request failed due to step validation",
              description: toHumanErrorMessage(error),
            });
          } else {
            open?.({
              type: "error",
              message: "Update geodata request failed",
              description: toHumanErrorMessage(error),
            });
          }
        },
      },
    );

    return response.data.id?.toString()!;
  }

  async function searchGeoDB(
    query: string,
    countryId?: string,
    offset = 0,
  ): Promise<GeodataGeoDbResultType[]> {
    if (!query) {
      return [];
    }

    const response = await custom?.({
      url: "https://wft-geo-db.p.rapidapi.com/v1/geo/places",
      method: "get",
      headers: {
        "x-rapidapi-host": "wft-geo-db.p.rapidapi.com",
        "x-rapidapi-key": "74d6a4de7cmsh9ea971ad83b0534p1bf0a6jsn8eb989a44d8c",
      },
      query: {
        namePrefix: query,
        // types: "ISLAND, CITY",
        sort: "-population",
        limit: 10,
        offset,
        ...(countryId ? { countryIds: countryId } : {}),
      },
    });

    const data = response?.data.data || [];

    return data.map((item: any) =>
      geodataGeoDbResultSchema.parse({
        ...item,
        geoDbId: item.id,
        geolocation: {
          latitude: item.latitude,
          longitude: item.longitude,
        },
      }),
    );
  }

  async function getTimezone(id: string): Promise<string> {
    const response = await custom?.({
      url: `https://wft-geo-db.p.rapidapi.com/v1/geo/cities/${id}`,
      method: "get",
      headers: {
        "x-rapidapi-host": "wft-geo-db.p.rapidapi.com",
        "x-rapidapi-key": "74d6a4de7cmsh9ea971ad83b0534p1bf0a6jsn8eb989a44d8c",
      },
    });

    return response?.data.data?.timezone?.replace("__", "/") || "";
  }

  return {
    get,
    search,
    searchNearest,
    getUsageCount,
    create,
    update,
    searchGeoDB,
    getTimezone,
    triggerEnrich,
  };
};
